// src/pages7-recipie-ai/NutritionComponent.js
import React, { useEffect, useState } from "react";

// Smooth CountUp animation hook for numbers
const useCountUp = (endVal, duration = 1000, trigger = true) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!trigger) {
      setCount(endVal);
      return;
    }
    let startTimestamp = null;
    let frameId;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(ease * endVal));
      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };
    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [endVal, duration, trigger]);

  return count;
};

// SVG Polar to Cartesian helper
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

// SVG Arc path generator
const describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const arcSweep = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, arcSweep, 0, end.x, end.y,
  ].join(" ");
};

// Vertical Striped Signal Meter (5 stacked horizontal pill bars)
const StripedSignalMeter = ({ filledBars = 3, totalBars = 5, color = "#eab308" }) => {
  return (
    <div className="striped-signal-meter" aria-hidden="true">
      {Array.from({ length: totalBars }).map((_, i) => {
        // Render from bottom up: i=0 is bottom
        const isFilled = i < filledBars;
        return (
          <span
            key={i}
            className="striped-meter-bar"
            style={{
              backgroundColor: isFilled ? color : "#e2e8f0",
              opacity: isFilled ? 1 : 0.65,
            }}
          />
        );
      })}
    </div>
  );
};

const NutritionComponent = ({ nutritionInfo, showTitle = false, titleOverride = "" }) => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 80);
    return () => clearTimeout(timer);
  }, [nutritionInfo]);

  // Helper to extract numbers cleanly from string values
  const parseNum = (val, fallback = 0) => {
    if (!val) return fallback;
    const match = String(val).match(/\d+(\.\d+)?/);
    return match ? Math.round(parseFloat(match[0])) : fallback;
  };

  const calNum = parseNum(nutritionInfo?.calories, 385);
  const carbsNum = parseNum(nutritionInfo?.carbs, 35);
  // Support sugar if provided, else fall back to protein
  const sugarNum = parseNum(nutritionInfo?.sugar || nutritionInfo?.protein, 8);
  const isSugar = Boolean(nutritionInfo?.sugar) || !nutritionInfo?.protein;
  const fatNum = parseNum(nutritionInfo?.fat, 22);

  // Animated Numbers
  const displayCal = useCountUp(calNum, 1100, animated);
  const displayCarbs = useCountUp(carbsNum, 950, animated);
  const displaySugar = useCountUp(sugarNum, 900, animated);
  const displayFat = useCountUp(fatNum, 850, animated);

  // Reference Targets
  const carbsTarget = 120;
  const sugarTarget = isSugar ? 50 : 65;
  const fatTarget = 78;

  // Signal Bar Fills (out of 5 bars)
  const carbsBars = Math.min(5, Math.max(1, Math.round((carbsNum / carbsTarget) * 5)));
  const sugarBars = Math.min(5, Math.max(1, Math.round((sugarNum / sugarTarget) * 5)));
  const fatBars = Math.min(5, Math.max(1, Math.round((fatNum / fatTarget) * 5)));

  // Title display
  const rawTitle = titleOverride || nutritionInfo?.title || "Food Item";
  const displayTitle = rawTitle.replace(/"/g, "").replace(/\*\*/g, "").trim();

  return (
    <div className="nutrition-showcase-container">
      {/* Optional Top Title matching mockup (when requested) */}
      {showTitle && (
        <div className="text-center mb-3">
          <span className="nutrition-pre-heading">Nutrition</span>
          <h2 className="nutrition-food-heading">{displayTitle}</h2>
        </div>
      )}

      {/* Main Two-Column Nutrition Visualizer */}
      <div className="nutrition-visual-row">
        {/* Left Column: Segmented Circular Calorie Gauge */}
        <div className="nutrition-ring-col">
          <div className="nutrition-ring-wrap">
            <svg viewBox="0 0 160 160" className="nutrition-ring-svg">
              {/* Arc 1: Green Segment (Top-Right to Right: 8° to 104°) */}
              <path
                d={describeArc(80, 80, 58, 8, 104)}
                fill="none"
                stroke="#22c55e"
                strokeWidth="11"
                strokeLinecap="round"
                className="nutrition-arc-green"
              />

              {/* Arc 2: Coral Red Segment (Bottom-Right to Bottom: 118° to 182°) */}
              <path
                d={describeArc(80, 80, 58, 118, 182)}
                fill="none"
                stroke="#ef4444"
                strokeWidth="11"
                strokeLinecap="round"
                className="nutrition-arc-red"
              />

              {/* Arc 3: Amber Yellow Segment (Left: 196° to 284°) */}
              <path
                d={describeArc(80, 80, 58, 196, 284)}
                fill="none"
                stroke="#f59e0b"
                strokeWidth="11"
                strokeLinecap="round"
                className="nutrition-arc-yellow"
              />

              {/* Arc 4: Soft Gray Segment (Top-Left Headroom: 298° to 354°) */}
              <path
                d={describeArc(80, 80, 58, 298, 354)}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="11"
                strokeLinecap="round"
                className="nutrition-arc-gray"
              />
            </svg>

            {/* Centered Ring Text & Flame */}
            <div className="nutrition-ring-center">
              <span className="nutrition-ring-flame" role="img" aria-label="calories">
                🔥
              </span>
              <span className="nutrition-ring-number">{displayCal}</span>
              <span className="nutrition-ring-unit">Kcal</span>
            </div>
          </div>
        </div>

        {/* Right Column: Vertical Striped Signal Meters */}
        <div className="nutrition-metrics-col">
          {/* 1. Carbs */}
          <div className="macro-metric-item">
            <StripedSignalMeter filledBars={carbsBars} totalBars={5} color="#eab308" />
            <div className="macro-metric-content">
              <span className="macro-metric-label carbs">Carbs</span>
              <div className="macro-metric-values">
                <span className="macro-num carbs">{displayCarbs}</span>
                <span className="macro-denom">/{carbsTarget}g</span>
              </div>
            </div>
          </div>

          {/* 2. Sugar or Protein */}
          <div className="macro-metric-item">
            <StripedSignalMeter filledBars={sugarBars} totalBars={5} color="#ef4444" />
            <div className="macro-metric-content">
              <span className="macro-metric-label sugar">
                {isSugar ? "Sugar" : "Protein"}
              </span>
              <div className="macro-metric-values">
                <span className="macro-num sugar">{displaySugar}</span>
                <span className="macro-denom">/{sugarTarget}g</span>
              </div>
            </div>
          </div>

          {/* 3. Fats */}
          <div className="macro-metric-item">
            <StripedSignalMeter filledBars={fatBars} totalBars={5} color="#22c55e" />
            <div className="macro-metric-content">
              <span className="macro-metric-label fats">Fats</span>
              <div className="macro-metric-values">
                <span className="macro-num fats">{displayFat}</span>
                <span className="macro-denom">/{fatTarget}g</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionComponent;
